import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { API } from '@/apiConfig';
import { isStaticCatalogOnly, fetchStaticCatalogProducts } from '@/utils/staticCatalog';
import { useTypewriterPlaceholder } from '@/hooks/useTypewriterPlaceholder';
import './hero/HeroV2.css';

const HERO_DEVICES = '/hero-v2/hero-devices.png?v=20260601d';

const SUGGEST_DEBOUNCE_MS = 260;
const SUGGEST_LIMIT = 12;

function SearchIcon() {
  return (
    <svg className="dm-hero-v2__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

function ProcessStep({ num, icon, title, subtitle }) {
  return (
    <li className="dm-hero-v2__step">
      <div className="flex items-center justify-center">
        <span className="dm-hero-v2__step-num">{num}</span>
      </div>
      <div className="dm-hero-v2__step-card">
        <span className="dm-hero-v2__step-icon">{icon}</span>
        <div className="dm-hero-v2__step-body">
          <strong>{title}</strong>
          <span>{subtitle}</span>
        </div>
      </div>
    </li>
  );
}

export default function HeroSection({ onSearch, onNavigate }) {
  const { t, lang, tl } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);

  const typewriterActive = searchTerm.trim().length === 0 && !suggestOpen;
  useTypewriterPlaceholder(searchInputRef, typewriterActive);

  const processSteps = useMemo(
    () => [
      {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12.5 22H18a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v9.5" />
            <path d="M14 2v4a2 2 0 0 0 2 2h4" />
            <path d="M13.378 15.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z" />
          </svg>
        ),
        title: tl('Submit RFQ', 'Demande de devis -RFQ', 'Enviar RFQ', 'إرسال طلب عرض'),
        subtitle: tl('Tell us what you need.', 'Exprimez précisément votre besoin.', 'Indíquenos lo que necesita.', 'أخبرنا بما تحتاجه.'),
      },
      {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
            <path d="M17 18h1" />
            <path d="M12 18h1" />
            <path d="M7 18h1" />
          </svg>
        ),
        title: tl(
          "Receive Manufacturer Quotations",
          'Réception des devis des fabricants',
          'Recibir cotizaciones del fabricante',
          'استلام عروض المصنع'
        ),
        subtitle: tl(
          "Directly from manufacturer's sales teams.",
          'Directement auprès des équipes commerciales des fabricants.',
          'Directamente del equipo comercial del fabricante.',
          'مباشرة من فرق مبيعات المصنع.'
        ),
      },
      {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            <path d="m9 14 2 2 4-4" />
          </svg>
        ),
        title: tl('Create Purchase Order', 'Création du bon de commande', 'Crear orden de compra', 'إنشاء أمر شراء'),
        subtitle: tl('Start order execution.', "Démarrage de l'exécution de la commande.", 'Iniciar la ejecución del pedido.', 'بدء تنفيذ الطلب.'),
      },
      {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7V5a2 2 0 0 1 2-2h2" />
            <path d="M17 3h2a2 2 0 0 1 2 2v2" />
            <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
            <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
            <circle cx="12" cy="12" r="3" />
            <path d="m16 16-1.9-1.9" />
          </svg>
        ),
        title: tl('Pre-Shipment Inspection', 'Contrôle qualité avant expédition', 'Inspección previa al envío', 'فحص ما قبل الشحن'),
        subtitle: tl('Reduce sourcing risk.', 'Pour garantir la conformité avant expédition.', 'Reducir el riesgo de abastecimiento.', 'تقليل مخاطر التوريد.'),
      },
      {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 10.189V14" />
            <path d="M12 2v3" />
            <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6" />
            <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-8.188-3.639a2 2 0 0 0-1.624 0L3 14a11.6 11.6 0 0 0 2.81 7.76" />
            <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1s1.2 1 2.5 1c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
          </svg>
        ),
        title: tl('Compare Freight Offers', 'Comparaison des offres de fret', 'Comparar ofertas de flete', 'مقارنة عروض الشحن'),
        subtitle: tl('With FreightIQ.', 'Avec FreightIQ.', 'Con FreightIQ.', 'مع FreightIQ.'),
      },
      {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19.07 4.93A10 10 0 0 0 6.99 3.34" />
            <path d="M4 6h.01" />
            <path d="M2.29 9.62A10 10 0 1 0 21.31 8.35" />
            <path d="M16.24 7.76A6 6 0 1 0 8.23 16.67" />
            <path d="M12 18h.01" />
            <path d="M17.99 11.66A6 6 0 0 1 15.77 16.67" />
            <circle cx="12" cy="12" r="2" />
            <path d="m13.41 10.59 5.66-5.66" />
          </svg>
        ),
        title: tl('Track Shipment Live', 'Suivi en temps réel', 'Seguimiento en vivo', 'تتبع الشحنة مباشرة'),
        subtitle: tl('Real-time visibility.', 'Visibilité en temps réel.', 'Visibilidad en tiempo real.', 'رؤية فورية.'),
      },
      {
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="m16 16 2 2 4-4" />
            <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14" />
            <path d="m7.5 4.27 9 5.15" />
            <polyline points="3.29 7 12 12 20.71 7" />
            <line x1="12" x2="12" y1="22" y2="12" />
          </svg>
        ),
        title: tl('Delivery', 'Livraison', 'Entrega', 'التسليم'),
        subtitle: tl('Goods delivered successfully.', 'Marchandises livrées avec succès.', 'Mercancía entregada con éxito.', 'تم تسليم البضائع بنجاح.'),
      },
    ],
    [tl],
  );

  useEffect(() => {
    const q = searchTerm.trim();
    if (q.length < 1) {
      setSuggestions([]);
      setSuggestOpen(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSuggestLoading(true);
      try {
        if (isStaticCatalogOnly()) {
          const all = await fetchStaticCatalogProducts();
          const qlow = q.toLowerCase();
          const list = all
            .filter((p) => {
              const blob = [p.name, p.name_tr, p.sku, p.category, p.subcategory, p.description]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              return blob.includes(qlow);
            })
            .slice(0, SUGGEST_LIMIT);
          setSuggestions(list);
          setSuggestOpen(list.length > 0);
          return;
        }
        const { data } = await axios.get(`${API}/products`, {
          params: { search: q, limit: SUGGEST_LIMIT },
        });
        const list = Array.isArray(data?.products) ? data.products : [];
        setSuggestions(list);
        setSuggestOpen(list.length > 0);
      } catch {
        setSuggestions([]);
        setSuggestOpen(false);
      } finally {
        setSuggestLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const onDown = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const selectSuggestion = useCallback(
    (product) => {
      const label = (product?.name || '').trim() || (product?.name_tr || '').trim();
      if (!label) return;
      setSearchTerm(label);
      setSuggestOpen(false);
      setSuggestions([]);
      onSearch(label);
    },
    [onSearch],
  );

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setSuggestOpen(false);
      onSearch(searchTerm.trim());
    }
  };

  const heroMapBg = `url(${process.env.PUBLIC_URL || ''}/hero-v2/hero-world-map.png?v=20260528c)`;

  return (
    <section
      className="dm-hero-v2"
      data-testid="hero-section"
      lang={lang === 'fr' || lang === 'es' ? lang : undefined}
      style={{ '--dm-hero-map-bg': heroMapBg }}
    >
      <div className="dm-hero-v2__container dm-hero-v2__grid">
        <div className="dm-hero-v2__left">
          <div className="dm-hero-v2__tag">
            {tl(
              'SMARTER SOURCING. SAFER IMPORTS. TOTAL CONTROL.',
              'Approvisionnement maîtrisé. Import sécurisé. Contrôle total.',
              'ABASTECIMIENTO MÁS INTELIGENTE. IMPORTACIONES MÁS SEGURAS. CONTROL TOTAL.',
              'توريد أذكى. استيراد أكثر أماناً. تحكم كامل.'
            )}
          </div>

          <h1 className="dm-hero-v2__title font-extrabold">
            {lang === 'fr' ? (
              <>
                {"La plateforme d'approvisionnement B2B et d'import"}
                <br />
                <span className="dm-hero-v2__title-accent">pour les acheteurs mondiaux</span>
              </>
            ) : (
              <>
                {tl('B2B Sourcing & Import', 'Approvisionnement B2B et import', 'Abastecimiento B2B e importación', 'توريد واستيراد B2B')}
                <br />
                {tl('Operating Platform', 'Plateforme opérationnelle', 'Plataforma operativa', 'منصة تشغيل')}
                <br />
                <span className="dm-hero-v2__title-accent">
                  {tl('for Global Buyers', 'pour les acheteurs mondiaux', 'para compradores globales', 'للمشترين العالميين')}
                </span>
              </>
            )}
          </h1>

          <p className="dm-hero-v2__desc">
            {t('heroSubtitle')}
            <strong className="dm-hero-v2__desc-bold">
              {tl('Less complexity. Less risk. More control.', 'Moins de complexité. Moins de risques. Plus de contrôle.', 'Menos complejidad. Menos riesgo. Más control.', 'تعقيد أقل. مخاطر أقل. تحكم أكبر.')}
            </strong>
          </p>

          <form className="dm-hero-v2__search" onSubmit={handleSearch} data-testid="hero-search-form">
            <label htmlFor="hero-search-input" className="dm-hero-v2__sr-only">
              {t('heroSearch')}
            </label>
            <div ref={searchWrapRef} className="dm-hero-v2__search-wrap">
              <SearchIcon />
              <input
                ref={searchInputRef}
                id="hero-search-input"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  if (suggestions.length > 0) setSuggestOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setSuggestOpen(false);
                }}
                autoComplete="off"
                className="dm-hero-v2__search-input"
                data-testid="hero-search-input"
                aria-expanded={suggestOpen}
                aria-controls="hero-search-suggestions"
                aria-autocomplete="list"
              />
              {suggestLoading && searchTerm.trim().length > 0 ? (
                <span
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-gray-200 border-t-[#0b1e4d]"
                  aria-hidden
                />
              ) : null}
              {suggestOpen && suggestions.length > 0 ? (
                <ul id="hero-search-suggestions" role="listbox" className="dm-hero-v2__suggestions">
                  {suggestions.map((p) => {
                    const title =
                      lang === 'ar' && (p.name_tr || '').trim()
                        ? p.name_tr.trim()
                        : (p.name || '').trim() || p.name_tr || p.sku || p.id;
                    const sub = [p.category, p.subcategory].filter(Boolean).join(' · ');
                    return (
                      <li key={p.id || p.sku || title} role="option">
                        <button
                          type="button"
                          className="dm-hero-v2__suggestion-btn"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectSuggestion(p)}
                        >
                          <span className="dm-hero-v2__suggestion-title">{title}</span>
                          {sub ? <span className="dm-hero-v2__suggestion-sub">{sub}</span> : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </form>

          <div className="dm-hero-v2__cta">
            <button
              type="button"
              className="dm-hero-v2__btn dm-hero-v2__btn-dark"
              onClick={() => onNavigate('products')}
              data-testid="hero-browse-all"
            >
              {t('heroCta')}
            </button>
          </div>

        </div>

        <div className="dm-hero-v2__right">
          <div className="dm-hero-v2__right-inner">
            <div className="dm-hero-v2__devices">
              <img
                src={HERO_DEVICES}
                alt={tl(
                  'DeMaxtore dashboard and shipment tracking on desktop and mobile',
                  'Tableau de bord DeMaxtore et suivi des expéditions',
                  'Panel DeMaxtore y seguimiento de envíos',
                  'لوحة DeMaxtore وتتبع الشحنات'
                )}
                width={1024}
                height={831}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
            <div className="dm-hero-v2__timeline-col">
              <h2 className="dm-hero-v2__diagram-title">
                {tl('How DeMaxtore Works', 'Comment fonctionne DeMaxtore', 'Cómo funciona DeMaxtore', 'كيف تعمل DeMaxtore')}
              </h2>
              <div className="dm-hero-v2__timeline-wrap">
                <div className="dm-hero-v2__timeline-line" aria-hidden />
                <ol className="dm-hero-v2__steps">
                  {processSteps.map((step, i) => (
                    <ProcessStep key={step.title} num={i + 1} {...step} />
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
